import { bind, classNames, Component, h, signal, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { AvatarStack } from "@/pages/project/features/directory/components/avatarStack";
import { useSchedule } from "@/pages/project/features/directory/store/queries/useSchedule";
import { ProjectProPaywall } from "@/pages/project/features/proPaywall";
import { ProjectEvents } from "@/pages/project/store/models";
import { useValidations } from "@/pages/project/store/queries/useValidations";
import { NWTableOfContents } from "@/pages/project/ui/tableOfContent";
import { CompletedEvent, ValidationEvents } from "@/components/validations/events";
import { installEditor } from "@/editor/install";
import { ScrollService } from "@/services/scrollService";
import { networkSignal } from "@/utils/signals";
import { DefaultApi, Project, ProjectStatus } from "@/api";
import { AppLayout } from "@/appLayout";
import {
  MissionButtonState,
  NWBadge,
  NWBlock,
  NWButton,
  NWEditorSkeleton,
  NWError,
  NWIcon,
  NWLoadingSpinner,
  NWMetabox,
  NWMissionBtn,
  NWNav,
  NWProgressBar,
  NWSuspense,
  NWTooltip,
  NWTrackManager,
  openModal,
} from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";
import { initApi } from "@/utils";

import { installPortfolio } from "../portfolio/install";
import styles from "./style.css?inline";

type Props = {
  standalone?: boolean;
};

@tag("pages-project")
export class PagesProject extends Component<Props> {
  static css = [tailwind, styles];

  private api: DefaultApi = initApi();
  private scrollService: ScrollService | undefined;

  private projectUpdate = networkSignal<Project>();

  private validationStore = useValidations();
  private missionAccomplishedState = signal<MissionButtonState>(MissionButtonState.Disabled);

  install() {
    if (Store.project.id.value) {
      Store.project.content.fetchTrackContent();

      import("@/services/analyticsService").then(({ useAnalyticsService }) => {
        useAnalyticsService().trackProjectViewed(Store.project.id.value!);
      });
    }

    useSchedule().fetch({});
  }

  installed() {
    if (Store.project.doesUserHaveAccess()) {
      this.setup();
    } else {
      addEventListener(ProjectEvents.PROJECT_UNLOCKED, this.setup);
    }

    // TODO ask Amber her thoughts on using a modal instead of the dialog
    // import("@/services/refreshService").then(({ RefreshService }) => {
    //   const refreshService = new RefreshService();
    //   const project = Store.project.getProject();
    //   refreshService.assessProjectReset(project);
    // });
  }

  @bind
  private scrollToItem(itemId: string): void {
    const scrollToEvent = new CustomEvent("scroll_to_step", { detail: { itemId }, cancelable: true });
    document.dispatchEvent(scrollToEvent);
  }

  @bind
  private setup() {
    this.validationStore.fetch(Store.project.id.value!).then(() => {
      this.missionAccomplishedState.value = this.calculateMissionAccomplishedState();
    });

    addEventListener(ValidationEvents.COMPLETED, this.handleValidationCompleted as EventListener);
    addEventListener(ValidationEvents.EDITED, this.handleValidationEdited as EventListener);
    addEventListener(ValidationEvents.OPTIONAL_FOCUSED, this.handleValidationOptionalFocused as EventListener);

    import("@/services/scrollService").then(({ ScrollService }) => {
      this.scrollService = new ScrollService();
      this.scrollService.startObservation();
    });
  }

  @bind
  private handleValidationCompleted(event: CustomEvent<CompletedEvent>) {
    const { id } = event.detail;
    this.validationStore.markAsComplete(id);
    this.missionAccomplishedState.value = this.calculateMissionAccomplishedState();
  }

  @bind
  private handleValidationEdited(event: CustomEvent<CompletedEvent>) {
    const { id } = event.detail;
    this.validationStore.markAsIncomplete(id);
    this.missionAccomplishedState.value = this.calculateMissionAccomplishedState();

    if (Store.project.isComplete()) {
      this.setMissionComplete(false, true);
    }
  }

  @bind
  private handleValidationOptionalFocused() {
    if (Store.project.isComplete()) {
      this.setMissionComplete(false, true);
      this.missionAccomplishedState.value = MissionButtonState.Enabled;
    }
  }

  @bind
  private async handleMissionComplete(): Promise<void> {
    if (!this.validationStore.hasAllValidationsComplete() || Store.project.isComplete()) {
      return;
    }

    const wasSuccessful = await this.setMissionComplete(true);

    if (!wasSuccessful) {
      return;
    }

    this.missionAccomplishedState.value = MissionButtonState.Complete;

    openModal("share", {
      projectId: Store.project.id.value!,
      linkedInPostData: Store.project.metadata.value?.sharetemplate,
    });

    if (Store.project.id.value) {
      import("@/services/analyticsService").then(({ useAnalyticsService }) => {
        useAnalyticsService().trackProjectComplete(Store.project.id.value!);
      });
    }
  }

  @bind
  private async setMissionComplete(state: boolean, updateImmediately: boolean = false): Promise<boolean> {
    const hasInProgressFeature = Store.features.hasFeature("projects.project_in_progress_status");
    const status = state
      ? ProjectStatus.PROJECT_COMPLETE
      : hasInProgressFeature
        ? ProjectStatus.PROJECT_IN_PROGRESS
        : ProjectStatus.PROJECT_INCOMPLETE;

    const event = new CustomEvent("project_updated", { detail: { projectId: Store.project.id.value!, status } });
    dispatchEvent(event);

    if (updateImmediately) {
      // Used to bypass the waiting period for the project status when we want the UI to update immediately, i.e. editing a validation
      Store.project.status.value = status;
    }

    const res = await this.projectUpdate.run(
      async () =>
        await this.api.submitProjectUpdate({
          projectId: Store.project.id.value!,
          projectUpdate: { status },
        }),
    );

    if (res == null) {
      return false;
    }

    if (res.status) {
      Store.project.setStatus(res.status);
    }

    return res.status === ProjectStatus.PROJECT_COMPLETE;
  }

  @bind
  private calculateMissionAccomplishedState(): MissionButtonState {
    const hasCompletedAllValidations = this.validationStore.hasAllValidationsComplete();
    if (hasCompletedAllValidations && Store.project.isComplete()) {
      return MissionButtonState.Complete;
    }

    if (hasCompletedAllValidations) {
      return MissionButtonState.Enabled;
    }

    return MissionButtonState.Disabled;
  }

  @bind
  private handleResetProject() {
    const projectId = Store.project.id.value;

    if (isEmpty(projectId)) {
      return;
    }

    openModal("projectReset", {
      projectId,
    });

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackProjectResetModalOpened(projectId!);
    });
  }

  renderPage() {
    const { metadata, completedBy, completions } = Store.project;
    const hasValidations = this.validationStore.hasValidations();
    const isProProject = Store.project.isProProject();
    const showProProjectPaywall = isProProject && !Store.project.isProProjectUnlockedForUser();
    const selectedTrack = Store.project.getSelectedTrackId();
    const isLoadingContent = Store.project.content.trackContent.signal.isLoading.value;
    const introContent = Store.project.content.introContent.signal;

    return (
      <>
        <NWProgressBar />

        <div>
          {showProProjectPaywall ? (
            <div class="fixed bottom-0 left-0 right-0 z-50">
              <ProjectProPaywall />
            </div>
          ) : (
            <div></div>
          )}
        </div>

        <div
          id="project-layout"
          class="page-layout xl:gap-16 2xl:gap-20 relative mx-auto sm:py-8 transition-opacity duration-300"
        >
          <div>{!Store.features.hasFeature("projects.navV2") && <NWNav />}</div>
          <div>
            <NWBlock>
              <div class="flex w-full flex-col items-start justify-start space-y-4 md:space-y-2 px-12">
                <NWBadge
                  withIndicator={!isProProject}
                  icon={isProProject ? "keyhole" : undefined}
                  iconSize={{ width: 9, height: 14 }}
                >
                  <span className="text-secondary">{isProProject ? "PRO-Project" : "Project"}</span>
                </NWBadge>

                <h1 class="flex content-center items-center pt-2 self-stretch text-4xl font-semibold">
                  {metadata.value?.title}
                </h1>

                <p class="flex-1 m-0 text-secondary">{metadata.value?.description}</p>

                <div class="flex items-center justify-between w-full">
                  <div class="pt-2 flex items-center space-x-2">
                    {completions.value === 0 && (
                      <div className="h-9 w-9 border rounded-full shrink-0 bg-secondary justify-center items-center inline-flex overflow-hidden">
                        <NWIcon name="medal-one" width={22} height={22} className="text-quaternary"></NWIcon>
                      </div>
                    )}
                    <AvatarStack
                      avatarUrls={completedBy.value ?? []}
                      completionCount={completions.value ?? 0}
                      size="md"
                      avatarCount={5}
                      withInviteLink
                    />
                  </div>

                  <div>
                    <NWTooltip content="Restart project" placement="top">
                      <NWButton
                        size="icon"
                        type="icon"
                        icon="refresh-clockwise"
                        iconSize="sm"
                        iconColor="text-primary"
                        onClick={this.handleResetProject}
                      />
                    </NWTooltip>
                  </div>
                </div>
              </div>

              <div class="flex py-10 mt-10 flex-col items-start gap-8 mx-14 self-stretch border-y border-gray-200">
                <div class="flex flex-wrap items-start gap-x-8 gap-y-4 md:gap-12">
                  <NWMetabox label="DIFFICULTY" text={metadata.value?.difficulty}>
                    <NWIcon slot="icon" name="terminal-square" />
                  </NWMetabox>
                  <NWMetabox label="TIME" text={metadata.value?.time}>
                    <NWIcon slot="icon" name="hourglass" />
                  </NWMetabox>
                  <NWMetabox label="COST" text={metadata.value?.cost}>
                    <NWIcon slot="icon" name="currency-dollar" />
                  </NWMetabox>
                </div>

                {metadata.value?.needs && (
                  <div class="flex flex-col md:flex-row w-full justify-between items-start">
                    <NWMetabox label="WHAT YOU'LL NEED">
                      <NWIcon slot="icon" name="tool" />
                    </NWMetabox>

                    <div class="w-full md:w-[60%] my-2 md:my-0">
                      <ul class="list-outside">
                        {metadata.value?.needs.map(value => (
                          <li class="text-secondary text-sm list-disc p-0" unsafeHTML={{ html: value }}></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {metadata.value?.concepts && (
                  <div class="flex flex-col md:flex-row w-full justify-between items-start">
                    <NWMetabox label="KEY CONCEPTS">
                      <NWIcon slot="icon" name="amazon" />
                    </NWMetabox>

                    <div class="w-full md:w-[60%] my-2 md:my-0">
                      <ul class="list-outside">
                        {metadata.value?.concepts.map(value => (
                          <li class="text-secondary text-sm list-disc p-0" unsafeHTML={{ html: value }}></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <div class="w-full mt-8">
                {Store.features.hasFeature("projects.editor") ? (
                  <NWSuspense
                    loadFunc={async () => {
                      const [introContent, NWEditor] = await Promise.all([
                        Store.project.content.fetchIntroContent(),
                        installEditor(),
                      ]);
                      return { introContent, NWEditor };
                    }}
                    loadingComponent={() => <NWEditorSkeleton />}
                    onLoadComplete={({ NWEditor }) => (
                      <>
                        {introContent.isLoading.value && <NWEditorSkeleton />}
                        {introContent.data.value && (
                          <NWEditor
                            instanceId="intro"
                            content={
                              Store.project.content.introContent.signal.data.value ??
                              "An error occured, please refresh the page and try again"
                            }
                            withScrollObservation={false}
                            withNodeHover={false}
                          />
                        )}
                        {introContent.error.value && <NWError />}
                      </>
                    )}
                  />
                ) : (
                  <div>
                    {introContent.isLoading.value && <NWLoadingSpinner />}
                    {introContent.data.value && (
                      <div id="intro-content" unsafeHTML={{ html: introContent.data.value }}></div>
                    )}
                  </div>
                )}
              </div>
              {introContent.data.value && (
                <div class={classNames("w-full", { "mb-[32vh]": showProProjectPaywall })}>
                  <NWTrackManager selected={selectedTrack} tracks={metadata.value?.tracks ?? []} />
                </div>
              )}
              {introContent.data.value && selectedTrack && !isLoadingContent && (
                <div class="my-10 w-full px-12">
                  <NWMissionBtn
                    className="mx-auto"
                    isLoading={this.projectUpdate.signal.isLoading.value}
                    state={this.missionAccomplishedState.value}
                    onClick={this.handleMissionComplete}
                    hasValidations={hasValidations}
                    disabledTooltip={`You still have tasks to complete, good luck 🙏`}
                  />
                  {this.projectUpdate.signal.error.value && (
                    <div class="mt-2">
                      <NWError />
                    </div>
                  )}
                </div>
              )}
            </NWBlock>
          </div>

          <div class="overflow-clip xl:max-w-md xl:min-w-48">
            <NWTableOfContents onItemSelect={this.scrollToItem} withIntersectionObservation />
          </div>
        </div>
      </>
    );
  }

  render() {
    if (this.props.standalone) {
      return this.renderPage();
    }

    return (
      <AppLayout>
        <NWSuspense
          slot="panel"
          loadFunc={installPortfolio}
          onLoadComplete={PagesPortfolio => <PagesPortfolio standalone />}
        />
        <div slot="main">{this.renderPage()}</div>
      </AppLayout>
    );
  }
}
